import { useEffect, useState } from 'react'
import { mediaApi } from '@/api/mediaApi'
import { getMediaBlob } from '@/db/viewerDb'
import { useAppStore } from '@/store/appStore'

export type MediaBlobState = 'loading' | 'ready' | 'error'

/**
 * Object URL for a media asset, read from the offline cache first and downloaded through the
 * authenticated API client otherwise. The URL is revoked when the file changes or on unmount.
 */
export function useMediaBlobUrl(systemFileName: string | null): { url: string | null; state: MediaBlobState } {
  const [result, setResult] = useState<{ name: string | null; url: string | null; state: MediaBlobState }>({
    name: null,
    url: null,
    state: 'loading',
  })

  useEffect(() => {
    if (!systemFileName) return

    let cancelled = false
    let objectUrl: string | null = null

    const load = async (): Promise<Blob> => {
      const cached = await getMediaBlob(systemFileName).catch(() => null)
      if (cached) return cached.blob
      if (useAppStore.getState().isOffline) throw new Error('Not cached for offline use')
      return mediaApi.downloadAsset(systemFileName)
    }

    load()
      .then((blob) => {
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setResult({ name: systemFileName, url: objectUrl, state: 'ready' })
      })
      .catch(() => {
        if (!cancelled) setResult({ name: systemFileName, url: null, state: 'error' })
      })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [systemFileName])

  if (!systemFileName || result.name !== systemFileName) return { url: null, state: 'loading' }
  return { url: result.url, state: result.state }
}
